import clsx from 'clsx';

/**
 * Button — action element.
 *
 * Props:
 *   variant   - "primary" (default) | "cta" | "secondary" | "ghost" | "danger"
 *               "cta" = the standardized "Book a Demo" primary action (deep
 *               royal-blue gradient, premium lift+shadow hover). Prefer the
 *               <BookDemoButton> wrapper for the demo CTA specifically.
 *   size      - "sm" | "md" (default) | "lg"
 *   icon      - leading icon element
 *   iconRight - trailing icon element
 *   href      - if provided, renders as <a>
 *   fullWidth - stretches to 100%
 */
const VARIANTS = {
  primary:   'bg-gradient-brand text-white border border-transparent shadow-button font-semibold hover:scale-[1.02]',
  cta:       'bg-gradient-cta text-white border border-transparent font-semibold shadow-cta hover:shadow-cta-hover hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand-dark)] focus-visible:ring-offset-2',
  secondary: 'bg-card text-ink border border-strong font-medium hover:bg-subtle',
  ghost:     'bg-card text-body border border-med font-medium hover:bg-subtle',
  danger:    'bg-danger-bg text-danger-dark border border-danger-bg font-medium hover:bg-red-100',
};

const SIZES = {
  sm: 'px-3 py-1.5 text-xs rounded-md',
  md: 'px-4 py-2.5 text-sm rounded-md gap-2',
  lg: 'px-7 py-3.5 text-[15px] rounded-pill gap-2',
};

export function Button({
  children,
  variant   = 'primary',
  size      = 'md',
  icon,
  iconRight,
  onClick,
  href,
  fullWidth,
  disabled  = false,
  type      = 'button',
  className = '',
  ...rest
}) {
  const classes = clsx(
    'group inline-flex items-center justify-center gap-2 font-sans no-underline cursor-pointer',
    'transition-[transform,box-shadow,opacity] duration-150',
    VARIANTS[variant],
    SIZES[size],
    fullWidth && 'w-full',
    disabled && 'opacity-60 pointer-events-none shadow-none',
    className,
  );

  // As a link: when disabled, drop href + flag for AT (no real <a> disabled).
  if (href) {
    return (
      <a
        href={disabled ? undefined : href}
        aria-disabled={disabled || undefined}
        className={classes}
        {...rest}
      >
        {icon}{children}{iconRight}
      </a>
    );
  }

  return (
    <button type={type} onClick={onClick} disabled={disabled} className={classes} {...rest}>
      {icon}{children}{iconRight}
    </button>
  );
}
